import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SearchOutlined, ReloadOutlined, MessageOutlined, SafetyCertificateOutlined, CopyOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';
import { handleTokenInvalidation } from '../utils/tokenHandler';

const Page = styled.section`
  --ink: ${({ theme }) => theme.isDarkMode ? '#edf0f5' : '#202631'};
  --muted: ${({ theme }) => theme.isDarkMode ? '#a0a8b6' : '#747d8b'};
  --line: ${({ theme }) => theme.isDarkMode ? '#343b46' : '#e9ecf1'};
  --soft: ${({ theme }) => theme.isDarkMode ? '#262c35' : '#f7f8fa'};
  width: min(1200px, calc(100% - 64px)); margin: 38px auto 56px; color: var(--ink); font-size: 14px;
  &, & * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif; }
  h1 { margin: 0; font-size: 28px; line-height: 1.35; font-weight: 700; letter-spacing: -.6px; }
  p { line-height: 1.65; }
  button { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 40px; padding: 0 15px; border: 1px solid var(--line); border-radius: 9px; font: inherit; font-weight: 500; cursor: pointer; background: ${({ theme }) => theme.cardBg}; color: var(--ink); transition: background .15s, border-color .15s; }
  button:hover:not(:disabled) { background: var(--soft); border-color: #bdc4cf; }
  button:disabled { cursor: not-allowed; opacity: .45; }
  button:focus-visible, input:focus-visible { outline: 3px solid #ff4d4f44; outline-offset: 2px; }
  button.primary { background: #ff4d4f; color: white; border-color: #ff4d4f; }
  button.primary:hover:not(:disabled) { background: #ec4247; border-color: #ec4247; }
  button.quiet { background: transparent; border-color: transparent; color: var(--muted); }
  .muted { color: var(--muted); }
  .notice { padding: 12px 26px; background: var(--soft); border-top: 1px solid var(--line); line-height: 1.6; }
  .error { color: #d93a45; }
  @media(max-width: 760px) { width: calc(100% - 32px); margin-top: 24px; h1 { font-size: 24px; } }
`;
const Heading = styled.div`
  display: flex; justify-content: space-between; gap: 20px; align-items: center; margin-bottom: 24px;
  .eyebrow { display: block; color: var(--muted); margin-bottom: 8px; font-size: 12px; letter-spacing: 1px; }
  p { color: var(--muted); margin-top: 9px; }
  .live { display: inline-flex; align-items: center; flex-shrink: 0; gap: 7px; padding: 7px 11px; border: 1px solid var(--line); border-radius: 20px; background: ${({ theme }) => theme.cardBg}; color: var(--muted); font-size: 12px; }
  .dot { width: 6px; height: 6px; border-radius: 100%; background: #3baf81; }
  @media(max-width: 760px) { align-items: flex-start; gap: 8px; p { max-width: 245px; } .live { margin-top: 26px; padding: 5px 8px; } }
`;
const Panel = styled.div`
  background: ${({ theme }) => theme.cardBg}; border: 1px solid var(--line); border-radius: 18px;
  box-shadow: 0 5px 24px #16243805; overflow: hidden;
  .list-heading { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 0 26px; min-height: 62px; border-bottom: 1px solid var(--line); }
  .tabs { display: flex; gap: 20px; }
  .tab { padding: 18px 1px; border: 0; border-radius: 0; background: transparent; color: var(--muted); border-bottom: 2px solid transparent; }
  .tab.active { color: var(--ink); border-bottom-color: #ff4d4f; font-weight: 650; }
  .tab .count { padding: 2px 6px; border-radius: 5px; font-size: 11px; background: var(--soft); }
  .total { color: var(--muted); font-size: 12px; }
  .column-head { display: grid; grid-template-columns: minmax(150px,1.4fr) 110px minmax(135px,1.2fr) 120px 126px; gap: 16px; padding: 12px 26px; color: var(--muted); font-size: 12px; background: var(--soft); }
  .panel-footer { border-top: 1px solid var(--line); padding: 13px 26px; display: flex; flex-wrap: wrap; gap: 8px; justify-content: space-between; color: var(--muted); font-size: 12px; }
  @media(max-width: 900px) { .column-head { display: none; } }
  @media(max-width: 760px) { border-radius: 14px; .list-heading { padding: 0 18px; gap: 6px; } .tabs { gap: 14px; } .panel-footer { padding: 13px 18px; } }
`;
const Tools = styled.form`
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 24px 26px 16px;
  .search { display: flex; align-items: center; gap: 10px; width: 370px; max-width: 100%; height: 44px; border: 1px solid var(--line); border-radius: 9px; background: var(--soft); padding: 0 13px; color: var(--muted); }
  .search:focus-within { border-color: #ff7779; box-shadow: 0 0 0 3px #ff4d4f10; }
  input { flex: 1; min-width: 0; width: 100%; font: inherit; background: transparent; border: 0; outline: 0; color: var(--ink); height: 100%; }
  input::placeholder { color: var(--muted); }
  .primary { min-width: 82px; height: 44px; }
  .refresh { margin-left: auto; }
  @media(max-width: 760px) { padding: 18px 18px 12px; gap: 8px; .search { width: calc(100% - 82px); } .primary { min-width: 74px; } .refresh { min-height: 34px; } }
`;
const Card = styled.article`
  display: grid; grid-template-columns: minmax(150px,1.4fr) 110px minmax(135px,1.2fr) 120px 126px; gap: 16px; align-items: center; padding: 22px 26px; border-top: 1px solid var(--line);
  strong { display: block; font-size: 16px; font-weight: 650; letter-spacing: .35px; font-variant-numeric: tabular-nums; }
  .sub { display: block; color: var(--muted); font-size: 12px; margin-top: 6px; line-height: 1.5; }
  .purpose { width: fit-content; padding: 5px 8px; border-radius: 6px; background: var(--soft); font-size: 12px; }
  .reset { background: ${({ theme }) => theme.isDarkMode ? '#49392d' : '#fff4e9'}; color: ${({ theme }) => theme.isDarkMode ? '#efbd86' : '#a36a2e'}; }
  .status { font-size: 13px; }
  .status.pending { color: ${({ theme }) => theme.isDarkMode ? '#79c5a0' : '#278764'}; }
  .validity { font-size: 13px; font-variant-numeric: tabular-nums; }
  .row-action button { width: 126px; font-size: 13px; }
  .code-panel { grid-column: 1 / -1; display: flex; align-items: center; flex-wrap: wrap; gap: 20px; padding: 18px 20px; border-radius: 10px; background: var(--soft); }
  .code-label { color: var(--muted); font-size: 12px; }
  .code { font-size: 30px; font-weight: 650; letter-spacing: 8px; font-variant-numeric: tabular-nums; }
  .code-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-left: auto; }
  @media(max-width: 900px) { grid-template-columns: 1fr auto; gap: 14px 12px; .purpose { justify-self: end; } .validity { text-align: right; } .row-action { grid-column: 1 / -1; } }
  @media(max-width: 760px) { padding: 20px 18px; .code-panel { padding: 15px; gap: 12px; } .code-actions { width: 100%; margin-left: 0; } .code { margin-left: auto; } }
`;
const Empty = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 48px 24px 52px; min-height: 275px;
  .illustration { display: grid; place-items: center; width: 76px; height: 76px; margin-bottom: 20px; border-radius: 24px; background: var(--soft); color: #adb5c1; font-size: 31px; }
  h2 { font-size: 17px; font-weight: 600; margin: 0 0 9px; }
  p { font-size: 13px; color: var(--muted); max-width: 370px; }
  button { margin-top: 18px; }
`;
const Help = styled.p`
  display: flex; align-items: flex-start; gap: 8px; color: var(--muted); font-size: 12px; margin: 16px 4px 0;
  .anticon { margin-top: 3px; font-size: 14px; }
`;
const stateNames = { pending: '待使用', verified: '已验证，等待完成注册', processing: '正在完成注册', used: '已使用', locked: '输错次数过多，已失效' };
const deliveryNames = { submitting: '短信提交中', accepted: '平台已受理（不代表已送达）', unconfirmed: '短信发送未确认，可人工提供' };
const formatTime = ms => new Date(ms).toLocaleTimeString('zh-CN', { hour12: false });
const secondsLeft = (expiresAt, now) => Math.max(0, Math.ceil((expiresAt - now) / 1000));

async function api(action, body) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('请先登录管理员账号');
    const response = await fetch(`https://server.battleverse.cn/admin/sms-codes/${action}.do`, {
      method: 'POST', cache: 'no-store', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body)
    });
    const result = await response.json();
    if (result.code === -2) handleTokenInvalidation();
    if (!response.ok || !result.success) throw new Error(result.msg || '无法读取，请确认管理员权限后重试');
    return result.data;
  } catch (error) {
    if (error.name === 'AbortError' || error instanceof SyntaxError) throw new Error('请求超时或登录已失效，请刷新后重试');
    throw error;
  } finally { clearTimeout(timer); }
}

export default function AdminSmsCodes() {
  const [phone, setPhone] = useState(''), [filter, setFilter] = useState('');
  const [rows, setRows] = useState([]), [total, setTotal] = useState(0);
  const [error, setError] = useState(''), [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false), [busy, setBusy] = useState('');
  const [shown, setShown] = useState(null), [now, setNow] = useState(Date.now());
  const [view, setView] = useState('all'), [loaded, setLoaded] = useState(false), [lastUpdated, setLastUpdated] = useState(null);
  const offset = useRef(0), sequence = useRef(0), actionSequence = useRef(0), mounted = useRef(true);

  const refresh = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true);
    try {
      const data = await api('list', { phoneNumber: filter });
      if (!mounted.current || request !== sequence.current) return;
      offset.current = data.serverTime - Date.now();
      setRows(data.items); setTotal(data.total); setNow(data.serverTime); setError('');
      setLoaded(true); setLastUpdated(data.serverTime);
      setShown(value => value && data.items.some(row => row.requestId === value.requestId && row.state === 'pending') ? value : null);
    } catch (failure) {
      if (mounted.current && request === sequence.current) { setError(failure.message); setShown(null); setRows([]); setLoaded(true); }
    } finally { if (mounted.current && request === sequence.current) setLoading(false); }
  }, [filter]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; sequence.current++; actionSequence.current++; };
  }, []);
  useEffect(() => {
    refresh();
    const poll = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 10000);
    const visibility = () => {
      actionSequence.current++; setShown(null); setBusy('');
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => { clearInterval(poll); document.removeEventListener('visibilitychange', visibility); sequence.current++; };
  }, [refresh]);
  useEffect(() => {
    const tick = setInterval(() => {
      const time = Date.now() + offset.current; setNow(time);
      setShown(value => value && value.expiresAt > time && value.hideAt > Date.now() ? value : null);
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const reveal = async (row, copy = false) => {
    if (busy) return;
    if (row.purpose === 'reset' && shown?.requestId !== row.requestId && !window.confirm(`这是 ${row.phoneNumber} 的找回密码验证码。请先通过已知联系方式或活动报名信息核实申请人本人身份，不能只凭对方报手机号给码。已核实，继续显示？`)) return;
    const action = ++actionSequence.current;
    setBusy(row.requestId); setShown(null); setNotice(''); setError('');
    try {
      const value = await api('reveal', { requestId: row.requestId });
      if (!mounted.current || action !== actionSequence.current || document.visibilityState !== 'visible') return;
      offset.current = value.serverTime - Date.now();
      setShown({ ...value, hideAt: Date.now() + 30000 }); setNow(value.serverTime);
      if (copy) {
        try { await navigator.clipboard.writeText(value.code); setNotice('验证码已复制，请仅发给已核实的申请人。'); }
        catch (_) { setNotice('浏览器不允许自动复制，请选中验证码手动复制。'); }
      }
    } catch (failure) { if (mounted.current && action === actionSequence.current) setError(failure.message); }
    finally { if (mounted.current && action === actionSequence.current) setBusy(''); }
  };

  const pending = rows.filter(row => row.state === 'pending' && row.expiresAt > now);
  const visibleRows = view === 'pending' ? pending : rows;
  const clearFilter = () => { setPhone(''); setFilter(''); setView('all'); setShown(null); setNotice(''); if (!filter) refresh(); };

  return <Page>
    <Heading>
      <div><span className="eyebrow">管理工具</span><h1>验证码管理</h1><p>收不到短信？在这里找到申请，人工协助用户完成验证。</p></div>
      <span className="live"><span className="dot" />自动更新</span>
    </Heading>
    <Panel>
      <Tools onSubmit={event => { event.preventDefault(); setShown(null); setNotice(''); setView('all'); if (filter === phone) refresh(); else setFilter(phone); }}>
        <label className="search"><SearchOutlined aria-hidden="true" /><input aria-label="查询手机号" inputMode="numeric" placeholder="输入手机号，查找验证码申请" value={phone} maxLength={11} onChange={event => setPhone(event.target.value.replace(/\D/g, ''))} /></label>
        <button className="primary" type="submit" disabled={loading}>查询</button>
        {filter && <button className="quiet" type="button" onClick={clearFilter} disabled={loading}>显示全部</button>}
        <button className="quiet refresh" type="button" onClick={refresh} disabled={loading}><ReloadOutlined spin={loading} aria-hidden="true" />{loading ? '刷新中…' : '刷新'}</button>
      </Tools>
      <div className="list-heading">
        <div className="tabs"><button type="button" className={`tab ${view === 'all' ? 'active' : ''}`} aria-pressed={view === 'all'} onClick={() => setView('all')}>全部申请</button><button type="button" className={`tab ${view === 'pending' ? 'active' : ''}`} aria-pressed={view === 'pending'} onClick={() => { setView('pending'); setShown(null); }}>待处理<span className="count">{pending.length}</span></button></div>
        <span className="total">共 {total} 条申请</span>
      </div>
      {notice && <div className="notice" role="status">{notice}</div>}
      {error && <p className="notice error" role="alert">{error}</p>}
      {!loaded && !error && <Empty><div className="illustration"><ReloadOutlined spin /></div><h2>正在获取申请</h2><p>请稍候</p></Empty>}
      {loaded && !error && visibleRows.length === 0 && <Empty>
        <div className="illustration"><MessageOutlined aria-hidden="true" /></div>
        <h2>{filter ? '未找到这个手机号的申请' : view === 'pending' ? '暂无待处理申请' : '暂时没有验证码申请'}</h2>
        <p>{filter ? '请确认手机号，并让用户在注册或找回密码页面重新申请。' : '用户申请验证码后，会自动显示在这里。'}</p>
        {filter && <button type="button" onClick={clearFilter}>清空查询</button>}
      </Empty>}
      {visibleRows.length > 0 && <div className="column-head" aria-hidden="true"><span>手机号 / 申请时间</span><span>验证用途</span><span>状态</span><span>有效时间</span><span>操作</span></div>}
      {visibleRows.map(row => {
      const remaining = secondsLeft(row.expiresAt, now);
      const visible = shown?.requestId === row.requestId && remaining > 0 && row.state === 'pending';
      return <Card key={row.requestId}>
        <div><strong>{row.phoneNumber}</strong><span className="sub">{formatTime(row.createdAt)} 申请</span></div>
        <span className={`purpose ${row.purpose === 'reset' ? 'reset' : ''}`}>{row.purpose === 'reset' ? '找回密码' : '新用户注册'}</span>
        <div><span className={`status ${remaining && row.state === 'pending' ? 'pending' : ''}`}>{remaining ? stateNames[row.state] : '已过期'}</span><span className="sub">{deliveryNames[row.delivery]}</span></div>
        <div className="validity">{row.state === 'pending' && remaining > 0 ? `剩余 ${Math.floor(remaining / 60)}分${String(remaining % 60).padStart(2, '0')}秒` : '—'}<span className="sub">{formatTime(row.expiresAt)} 到期</span></div>
        <div className="row-action"><button type="button" disabled={!!busy || !remaining || row.state !== 'pending'} onClick={() => reveal(row)}><EyeOutlined aria-hidden="true" />{busy === row.requestId ? '读取中…' : '查看验证码'}</button></div>
        {visible && <div className="code-panel"><div className="code-label">当前验证码<span className="sub">30秒后自动隐藏</span></div><span className="code" aria-label="当前验证码">{shown.code}</span><div className="code-actions"><button className="primary" type="button" disabled={!!busy} onClick={() => reveal(row, true)}><CopyOutlined aria-hidden="true" />复制验证码</button><button className="quiet" type="button" onClick={() => setShown(null)}><CloseOutlined aria-hidden="true" />隐藏</button></div></div>}
      </Card>;
      })}
      <div className="panel-footer"><span>最近10分钟的申请 · 每10秒刷新{total > 100 ? ' · 当前显示最新100条，请按手机号查询' : ''}</span><span>{lastUpdated ? `更新于 ${formatTime(lastUpdated)}` : '正在连接'}</span></div>
    </Panel>
    <Help><SafetyCertificateOutlined aria-hidden="true" />验证码仅提供给申请人本人。找回密码前请先核实身份；使用、重新申请或到期后，原码失效。</Help>
  </Page>;
}
